/**
 * AIService — client frontend vers le backend Python UNIQUE (FastAPI).
 *
 * Le backend relaie tous les appels vers OpenRouter pour que la clé API
 * ne soit jamais exposée au navigateur / à Electron.
 *
 * En développement le backend tourne sur http://127.0.0.1:8787.
 * En production (Electron), l'URL sera injectée via VITE_AI_BACKEND_URL.
 */
import type { Quadrant, TaskSource } from '@store/types';
import { getToken } from '@store/userStore';

const BACKEND_URL =
  import.meta.env.VITE_AI_BACKEND_URL ?? 'http://127.0.0.1:8787';

/* -------------------------------------------------------------------------- */
/* Types de contrat avec le backend                                           */
/* -------------------------------------------------------------------------- */

export interface BriefingPayload {
  first_name: string;
  email_count: number;
  call_count: number;
  tasks: {
    title: string;
    source: TaskSource;
    due_in_hours: number | null;
    description?: string;
  }[];
}

export interface BriefingResult {
  summary: string;
  key_actions: string[];
}

export interface PriorityPayload {
  title: string;
  description?: string;
  source: TaskSource;
  due_in_hours: number | null;
  current_hour: number;
}

export interface PriorityResult {
  score: number;
  quadrant: Quadrant;
  rationale: string;
}

export interface ClassifyPayload {
  filename: string;
  content_preview: string;
  known_projects: string[];
}

export interface ClassifyResult {
  project: string;
  task: string;
  confidence: number;
}

export interface AppsSuggestPayload {
  task_title: string;
  task_description?: string;
}

export interface AppsSuggestResult {
  apps: string[];
}

export type AppKind =
  | 'email'
  | 'word'
  | 'excel'
  | 'powerpoint'
  | 'onenote'
  | 'teams'
  | 'zoom'
  | 'calendar'
  | 'calculator'
  | 'browser'
  | 'unknown';

export interface DraftPayload {
  task_title: string;
  task_description?: string;
  app_kind: AppKind;
  user_name?: string;
  recipient_hint?: string;
}

export interface EmailDraft {
  to: string | null;
  subject: string;
  body: string;
}

export interface DraftResult {
  summary: string;
  email: EmailDraft | null;
  clipboard_text: string | null;
}

export interface SmartSortFilePayload {
  id: string;
  filename: string;
  preview: string;
  current_folder?: string | null;
}

export interface SmartSortPayload {
  criteria: string;
  files: SmartSortFilePayload[];
  known_folders?: string[];
}

export interface SmartSortDecision {
  file_id: string;
  target_folder: string;
  reason: string;
}

export interface SmartSortResult {
  summary: string;
  decisions: SmartSortDecision[];
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

class AIServiceError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'AIServiceError';
  }
}

async function postJson<TBody, TResp>(path: string, body: TBody): Promise<TResp> {
  let res: Response;
  const token = getToken();
  try {
    res = await fetch(`${BACKEND_URL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new AIServiceError(
      `Backend IA inaccessible (${BACKEND_URL}${path}). Lancez : uvicorn main:app --port 8787.`,
    );
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new AIServiceError(
      `Erreur backend IA ${res.status} : ${text || res.statusText}`,
      res.status,
    );
  }
  return (await res.json()) as TResp;
}

/* -------------------------------------------------------------------------- */
/* Service public                                                             */
/* -------------------------------------------------------------------------- */

export const AIService = {
  /** Vérifie que le backend répond (et renvoie le modèle OpenRouter actif). */
  async health(): Promise<{ status: string; model: string }> {
    const res = await fetch(`${BACKEND_URL}/health`);
    if (!res.ok) throw new AIServiceError('Health check échoué', res.status);
    return res.json();
  },

  /** Génère un briefing matinal personnalisé. */
  generateBriefing(payload: BriefingPayload): Promise<BriefingResult> {
    return postJson<BriefingPayload, BriefingResult>('/ai/briefing', payload);
  },

  /** Calcule score + quadrant Eisenhower pour une tâche. */
  scorePriority(payload: PriorityPayload): Promise<PriorityResult> {
    return postJson<PriorityPayload, PriorityResult>('/ai/priority', payload);
  },

  /** Classe un fichier dans l'arborescence projets/tâches. */
  classifyFile(payload: ClassifyPayload): Promise<ClassifyResult> {
    return postJson<ClassifyPayload, ClassifyResult>('/ai/classify', payload);
  },

  /** Suggère les applications à ouvrir pour une tâche. */
  suggestApps(payload: AppsSuggestPayload): Promise<AppsSuggestResult> {
    return postJson<AppsSuggestPayload, AppsSuggestResult>('/ai/apps', payload);
  },

  /** Génère un brouillon pré-rempli (email, tableau Excel, document Word…). */
  draftContent(payload: DraftPayload): Promise<DraftResult> {
    return postJson<DraftPayload, DraftResult>('/ai/draft', payload);
  },

  /** Range un lot de fichiers en appliquant des critères libres. */
  smartSort(payload: SmartSortPayload): Promise<SmartSortResult> {
    return postJson<SmartSortPayload, SmartSortResult>('/ai/smart-sort', payload);
  },

  /** Chat libre (passe-plat vers OpenRouter). */
  chat(messages: { role: 'system' | 'user' | 'assistant'; content: string }[]): Promise<{
    content: string;
  }> {
    return postJson('/ai/chat', { messages, temperature: 0.4, max_tokens: 800 });
  },
};

export { AIServiceError };
